// Pre-signup "Who can fund my project?" quiz — options + likelihood preview.
import { bandForScore } from "./quizPreliminaryScore";
import { convertQuizCompletion } from "../components/quiz/ShareScore";

export const PROJECT_QUIZ_STORAGE_KEY = "clinq_project_quiz_v1";

export const PROJECT_CATEGORIES = [
  { id: "real_estate",    label: "Real Estate / Property Development" },
  { id: "energy",         label: "Energy (Solar, Hydro, Wind, Biomass)" },
  { id: "infrastructure", label: "Infrastructure (Roads, Water, Logistics)" },
  { id: "agri_project",   label: "Agri-Project (Farm Development, Processing)" },
  { id: "manufacturing",  label: "Manufacturing Facility" },
  { id: "hospitality",    label: "Hospitality Build (Hotel, Lodge)" },
  { id: "other",          label: "Other capital project" },
];

export const PROJECT_STAGES = [
  { id: "s1", label: "Concept", hint: "Idea with preliminary numbers" },
  { id: "s2", label: "Feasibility", hint: "Feasibility study done or underway" },
  { id: "s3", label: "Development", hint: "Land secured, permits underway" },
  { id: "s4", label: "Ready for financial close", hint: "All approvals in place" },
  { id: "s5", label: "Under construction", hint: "Build in progress" },
  { id: "s6", label: "Operational", hint: "Built and generating revenue" },
];

export const LAND_OPTIONS = [
  { id: "title",          label: "Registered title in our name" },
  { id: "lease",          label: "Registered lease" },
  { id: "sale_agreement", label: "Signed sale agreement / binding option" },
  { id: "option",         label: "Option under negotiation" },
  { id: "not_secured",    label: "Not yet secured" },
];

export const OFFTAKE_OPTIONS = [
  { id: "gte60",     label: "60% or more", hint: "Signed PPA / pre-sales / offtake agreements" },
  { id: "p30_59",    label: "30% – 59%" },
  { id: "lt30",      label: "Under 30%" },
  { id: "none",      label: "Nothing committed yet" },
  { id: "too_early", label: "Too early to say" },
];

export const EQUITY_OPTIONS = [
  { id: "gte30",  label: "30% or more of total project cost" },
  { id: "p20_29", label: "20% – 29%" },
  { id: "p10_19", label: "10% – 19%" },
  { id: "lt10",   label: "Under 10%" },
];

export const PROJECT_RAISE_BRACKETS = [
  { id: "under_1m",  label: "Under KES 1M" },
  { id: "1m_5m",     label: "KES 1M – 5M" },
  { id: "5m_15m",    label: "KES 5M – 15M" },
  { id: "15m_25m",   label: "KES 15M – 25M" },
  { id: "25m_50m",   label: "KES 25M – 50M" },
  { id: "50m_100m",  label: "KES 50M – 100M" },
  { id: "100m_500m", label: "KES 100M – 500M" },
  { id: "over_500m", label: "Over KES 500M" },
];

const STAGE_PTS = { s1: 5, s2: 10, s3: 15, s4: 20, s5: 20, s6: 22 };
const LAND_PTS = { title: 25, lease: 22, sale_agreement: 18, option: 8, not_secured: 0 };
const OFFTAKE_PTS = { gte60: 25, p30_59: 15, lt30: 8, none: 0, too_early: 5 };
const EQUITY_PTS = { gte30: 25, p20_29: 18, p10_19: 10, lt10: 0 };

export function computeProjectLikelihood(draft) {
  const score =
    (STAGE_PTS[draft.stage] || 0) + (LAND_PTS[draft.land] || 0) +
    (OFFTAKE_PTS[draft.offtake] || 0) + (EQUITY_PTS[draft.equity] || 0);
  const level = score >= 60 ? "high" : score >= 35 ? "medium" : "low";
  const hints = [];
  if ((LAND_PTS[draft.land] || 0) < 18)
    hints.push("Secure land control — a registered title, lease or binding sale agreement is the single biggest project-finance gate.");
  if ((OFFTAKE_PTS[draft.offtake] || 0) < 15)
    hints.push("Lock in committed revenue — signed offtake, PPA, pre-sale or pre-let agreements move funders from interested to serious.");
  if ((EQUITY_PTS[draft.equity] || 0) < 10)
    hints.push("Grow your sponsor equity — funders expect at least 10% of total project cost contributed, and 30% earns full marks.");
  if ((STAGE_PTS[draft.stage] || 0) <= 10)
    hints.push("Advance your feasibility work — an independent feasibility study unlocks the next readiness band.");
  return { score, level, hints: hints.slice(0, 3) };
}

export function readProjectQuizDraft() {
  try {
    const raw = localStorage.getItem(PROJECT_QUIZ_STORAGE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch (_) { return null; }
}

// Login handoff (mirrors founderQuizDraft): fill blanks on an existing project
// from a completed draft; never creates a venture. Draft cleared on success.
export async function applyProjectQuizDraft(apiClient) {
  const draft = readProjectQuizDraft();
  if (!draft || !draft.completed_at) return false;
  try {
    const { data } = await apiClient.post("/project/bootstrap", { quiz: draft, merge_only_empty: true });
    try { localStorage.removeItem(PROJECT_QUIZ_STORAGE_KEY); } catch (_) { /* ignore */ }
    convertQuizCompletion(apiClient);
    return !data?.skipped;
  } catch (_) { return false; }
}

// ---- Preliminary Project Readiness Score (0–100) ----
const RAISE_MID_KES = {
  under_1m: 500_000, "1m_5m": 3_000_000, "5m_15m": 10_000_000, "15m_25m": 20_000_000,
  "25m_50m": 37_500_000, "50m_100m": 75_000_000, "100m_500m": 300_000_000, over_500m: 750_000_000,
};
const STAGE_RANK = { s1: 1, s2: 2, s3: 3, s4: 4, s5: 5, s6: 6 };
const LAND_CONTROL = new Set(["title", "lease", "sale_agreement"]);
const MAX_PTS = 22 + 25 + 25 + 25;

export function computeProjectPreliminaryScore(draft) {
  const { score: pts, hints } = computeProjectLikelihood(draft);
  if (!draft.stage || !draft.land || !draft.offtake || !draft.equity) return null;
  let score = (pts / MAX_PTS) * 100;
  const raise = RAISE_MID_KES[draft.raise] || 0;
  const rank = STAGE_RANK[draft.stage] || 1;
  if (rank <= 2 && raise >= 300_000_000) score -= 14;
  else if (rank <= 2 && raise >= 75_000_000) score -= 8;
  if (draft.equity === "lt10" && raise >= 37_500_000) score -= 5;
  if (draft.stage === "s1" && draft.land === "not_secured" && draft.offtake === "none") score = Math.min(score, 25);
  score = Math.max(8, Math.min(96, Math.round(score)));
  return { score, band: bandForScore(score), hints };
}

// ---- Funder-type preview for projects ----
const INFRA_CATS = new Set(["energy", "infrastructure", "agri_project"]);
const OFFTAKE_RANK = { gte60: 3, p30_59: 2, lt30: 1, too_early: 0, none: 0 };
const EQUITY_RANK = { gte30: 3, p20_29: 2, p10_19: 1, lt10: 0 };

export function scoreProjectFunderPreview(d) {
  const land = LAND_CONTROL.has(d.land);
  const rank = STAGE_RANK[d.stage] || 1;
  const off = OFFTAKE_RANK[d.offtake] ?? 0;
  const eq = EQUITY_RANK[d.equity] ?? 0;
  const raise = RAISE_MID_KES[d.raise] || 0;
  const infra = INFRA_CATS.has(d.category);
  const rows = [
    {
      id: "commercial_bank", label: "Commercial banks",
      blurb: "Construction and term debt secured on land, contracts and sponsor guarantees.",
      likelihood: land && off >= 2 && rank >= 3 && eq >= 2 ? "high" : land && (off >= 1 || rank >= 3) ? "medium" : "low",
      rationale: land ? "Land control is in place — banks now look at committed revenue and your equity share."
        : "Banks will not lend against land you do not yet control.",
    },
    {
      id: "dfi", label: "DFIs & development banks",
      blurb: "Long-tenor debt for energy, infrastructure and agri-projects with development impact.",
      likelihood: infra && land && rank >= 3 && off >= 2 && raise >= 75_000_000 ? "high"
        : (infra && (land || off >= 2)) || (land && off >= 2 && raise >= 300_000_000) ? "medium" : "low",
      rationale: infra ? "Your sector is core DFI territory — permits, offtake and ticket size decide timing."
        : "DFIs focus on energy, infrastructure and agri — other sectors need a strong impact case.",
    },
    {
      id: "infra_fund", label: "Project-finance & infrastructure funds",
      blurb: "Specialist funds financing large, contracted assets at or near financial close.",
      likelihood: raise >= 300_000_000 && rank >= 3 && land && off >= 2 ? "high"
        : raise >= 37_500_000 && land ? "medium" : "low",
      rationale: raise >= 300_000_000 ? "Your ticket size is in range — they will screen offtake and readiness for close."
        : "Most infrastructure funds start at KES 100M+ tickets — smaller raises fit banks better.",
    },
    {
      id: "impact_climate", label: "Impact & climate funds",
      blurb: "Blended and concessional capital for projects with climate or community outcomes.",
      likelihood: infra && rank >= 2 && land ? "high" : infra ? "medium" : "low",
      rationale: infra ? "Energy, water and agri projects are exactly what these funds are set up to back."
        : "Property and hospitality builds rarely qualify unless there is a clear impact angle.",
    },
    {
      id: "equity_developer", label: "Development-equity partners",
      blurb: "Co-developers and equity investors who take early-stage project risk for a share of upside.",
      likelihood: eq >= 2 && (land || d.land === "option") && rank >= 2 && rank <= 4 ? "high"
        : rank <= 4 && (land || d.land === "option") ? "medium" : "low",
      rationale: rank <= 4 ? "Equity partners come in before financial close — your own skin in the game is what they price."
        : "Once a project is built, equity partners look for operating-yield deals instead.",
    },
    {
      id: "grant_blended", label: "Grant & blended-finance facilities",
      blurb: "Development grants, first-loss and technical-assistance facilities for early-stage projects.",
      likelihood: infra && rank <= 3 && raise < 75_000_000 ? "high" : infra && rank <= 4 ? "medium" : "low",
      rationale: infra ? "Early-stage energy, water and agri projects can access feasibility and de-risking grants."
        : "Grant facilities focus on public-good sectors — commercial property and hospitality sit outside them.",
    },
  ];
  const order = { high: 0, medium: 1, low: 2 };
  return rows.sort((a, b) => order[a.likelihood] - order[b.likelihood]);
}
